import { Fragment, useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useMMKVBoolean } from 'react-native-mmkv';

import { Text, VStack, theme } from '@squire/ui';

import type { UserCards } from '@/api/customer/getCustomerCardsQuery/types';
import { ApplePayBadgeIcon } from '@/components/_icons/ApplePayBadgeIcon';
import { BnplIcon } from '@/components/_icons/BnplIcon';
import { CardBadgeIcon } from '@/components/_icons/CardBadgeIcon';
import { CardBrandIcon } from '@/components/_icons/CardBrandIcon';
import { CashAppIcon } from '@/components/_icons/CashAppIcon';
import { PayInPersonBadgeIcon } from '@/components/_icons/PayInPersonBadgeIcon';
import { InfoNotice } from '@/components/_shared/InfoNotice';
import { storage, storageKeys } from '@/constants/storage';
import { useAvailablePaymentMethods } from '@/hooks/payments/useAvailablePaymentMethods';
import { useBnplExperimentVariant } from '@/hooks/payments/useBnplExperimentVariant';
import { useDigitalWalletPay } from '@/hooks/payments/useDigitalWalletPay';
import { useShouldShowPrepaymentNotice } from '@/hooks/payments/useShouldShowPrepaymentNotice';
import { useCustomerQuery } from '@/hooks/queries/customer/useCustomerQuery';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import {
  type PaymentMethod,
  PaymentMethodType,
  getBnplProvider,
  getTrackingMethod,
  isPaymentMethodBnpl,
  isPaymentMethodCardGeneric,
  isPaymentMethodCardSaved,
} from '@/utils/paymentMethods';
import { Tracker } from '@/utils/tracking';
import {
  BNPL_PROVIDER_UNKNOWN,
  LD_EVENTS,
  type LdCommonPayload,
  type LdPaymentMethodImpressionPayload,
  type LdPaymentMethodSelectedPayload,
  buildLdSeenFlags,
} from '@/utils/tracking/ldEvents';

import { BnplPaymentMethodRow } from './BnplPaymentMethodRow';
import { PaymentMethodOption } from './PaymentMethodOption';
import { PaymentMethodSection } from './PaymentMethodSection';
import type { PaymentBadgeIcon, PaymentMethodSelectorProps } from './types';
import {
  BNPL_PROVIDER_LABEL_KEYS,
  collapseBnplRows,
  convertUserCardToPaymentMethod,
  getDefaultPaymentMethod,
  getPaymentMethodKey,
  isPaymentMethodEqual,
  isUserCard,
  toLdSeenMethods,
} from './utils';

const getPaymentMethodIcon = (
  method: PaymentMethod | UserCards[0],
): PaymentBadgeIcon | null => {
  const paymentMethod: PaymentMethod =
    'type' in method && 'id' in method
      ? method
      : convertUserCardToPaymentMethod(method);

  if (paymentMethod.type === PaymentMethodType.applePay) {
    return <ApplePayBadgeIcon width={38} height={24} />;
  }
  if (paymentMethod.type === PaymentMethodType.googlePay) {
    return <CardBrandIcon brand="googlepay" />;
  }
  if (
    paymentMethod.type === PaymentMethodType.card ||
    (paymentMethod.type === PaymentMethodType.cardless &&
      paymentMethod.id !== 'bnpl' &&
      paymentMethod.id !== 'cashapp')
  ) {
    return <CardBadgeIcon width={38} height={24} />;
  }
  if (paymentMethod.type === PaymentMethodType.payInPerson) {
    return <PayInPersonBadgeIcon width={38} height={24} />;
  }
  if (
    paymentMethod.type === PaymentMethodType.cardless &&
    paymentMethod.id === 'cashapp'
  ) {
    return <CashAppIcon width={38} height={24} />;
  }
  if (
    paymentMethod.type === PaymentMethodType.cardless &&
    paymentMethod.id === 'bnpl'
  ) {
    return <BnplIcon width={38} height={24} />;
  }
  return null;
};

const PaymentMethodSelector = ({
  cart,
  shop,
  isWaitlistMode = false,
  waitlistRequiresPayment = false,
  excludePaymentTypes = [],
  variant = 'default',
  bnplVariant = 'control',
  isSheetOpen = false,
  hideSectionTitle = false,
  includeSavedCards = true,
  preselectDefaultMethod = false,
}: PaymentMethodSelectorProps) => {
  const fieldName = 'paymentMethod';
  const { t } = useScreenTranslations('ConfirmBookingBottomSheet');

  const { setValue } = useFormContext<{
    paymentMethod: PaymentMethod | undefined;
  }>();
  const selectedMethod = useWatch({ name: fieldName, exact: true });

  const showPrepaymentNoticeFromHook = useShouldShowPrepaymentNotice({
    shop,
    cart: cart ?? null,
    selectedPaymentMethod: selectedMethod as PaymentMethod | undefined,
  });

  const isWaitlist = useMemo(() => {
    return (
      isWaitlistMode ||
      (cart?.items.some((item) => item.bookedFromWaitingList) ?? false)
    );
  }, [isWaitlistMode, cart?.items]);

  const allPaymentMethods = useAvailablePaymentMethods(cart, shop, isWaitlist, {
    includeSavedCards,
  });

  const paymentMethods = useMemo(() => {
    const visible =
      excludePaymentTypes.length === 0
        ? allPaymentMethods
        : allPaymentMethods.filter((method) => {
            const normalized = method as PaymentMethod | UserCards[0];
            const pm: PaymentMethod = isUserCard(normalized)
              ? convertUserCardToPaymentMethod(normalized)
              : normalized;
            return !excludePaymentTypes.includes(pm.type);
          });

    return bnplVariant === 'control'
      ? visible
      : collapseBnplRows(visible as (PaymentMethod | UserCards[0])[]);
  }, [allPaymentMethods, excludePaymentTypes, bnplVariant]);

  const hasBnplMethod = useMemo(
    () =>
      paymentMethods.some(
        (method) =>
          !isUserCard(method as PaymentMethod | UserCards[0]) &&
          isPaymentMethodBnpl(method as PaymentMethod),
      ),
    [paymentMethods],
  );

  const effectiveBnplVariant = hasBnplMethod ? bnplVariant : 'control';
  const isTreatment = effectiveBnplVariant !== 'control';

  const showPrepaymentNoticeForVariant =
    variant === 'giftCard'
      ? !!selectedMethod &&
        (selectedMethod as PaymentMethod).type !== PaymentMethodType.payInPerson
      : showPrepaymentNoticeFromHook;

  const showPrepaymentNotice =
    showPrepaymentNoticeForVariant &&
    !(isTreatment && isPaymentMethodBnpl(selectedMethod as PaymentMethod));

  // Auto-select payment method when there's only one option
  useEffect(() => {
    if (paymentMethods.length === 1 && !selectedMethod) {
      const singleMethod = paymentMethods[0] as PaymentMethod | UserCards[0];
      const paymentMethod: PaymentMethod = isUserCard(singleMethod)
        ? convertUserCardToPaymentMethod(singleMethod)
        : singleMethod;

      setValue(fieldName, paymentMethod, { shouldValidate: true });
    }
  }, [paymentMethods, selectedMethod, setValue]);

  const { data: customer } = useCustomerQuery();

  const [applePayIsDefault] = useMMKVBoolean(
    `${storageKeys.applePayIsDefault}:${customer?.id ?? ''}`,
    storage,
  );
  const { isLoading: isApplePayLoading } = useDigitalWalletPay('applePay');
  const hasPreselectedRef = useRef(false);

  // Runs once per mount so deselecting the default row is not undone. Waits
  // for a customer (a guest may sign in mid-sheet) and Apple Pay availability.
  useEffect(() => {
    if (!preselectDefaultMethod || hasPreselectedRef.current) return;
    if (selectedMethod) {
      hasPreselectedRef.current = true;
      return;
    }
    if (!customer) return;
    if (applePayIsDefault && isApplePayLoading) return;

    hasPreselectedRef.current = true;
    const defaultMethod = getDefaultPaymentMethod(
      paymentMethods as (PaymentMethod | UserCards[0])[],
      !!applePayIsDefault,
    );
    if (defaultMethod) {
      setValue(fieldName, defaultMethod, { shouldValidate: true });
    }
  }, [
    preselectDefaultMethod,
    selectedMethod,
    customer,
    applePayIsDefault,
    isApplePayLoading,
    paymentMethods,
    setValue,
  ]);

  // SQR-14053: the experiment covers booking checkout only, and its hook reads
  // the LaunchDarkly flag — which registers an exposure. Hosts that never fire
  // an impression must not enroll units, so the cart (the hook's eligibility
  // input) is withheld until the host reports its sheet open.
  const {
    variant: experimentVariant,
    isEnrolled,
    eligibleMethods,
  } = useBnplExperimentVariant(isSheetOpen ? cart : undefined, shop);

  const ldCommonPayload: LdCommonPayload = useMemo(
    () => ({
      variant: experimentVariant,
      shop_id: shop?.id ?? '',
      entry_type: customer ? 'authenticated' : 'guest',
    }),
    [experimentVariant, shop?.id, customer],
  );

  const shownLdMethods = useMemo(
    () =>
      toLdSeenMethods(
        paymentMethods as (PaymentMethod | UserCards[0])[],
        eligibleMethods,
      ),
    [paymentMethods, eligibleMethods],
  );

  const hasTrackedLdImpressionRef = useRef(false);

  // Fires once per sheet-open cycle, and only once `isEnrolled` holds — which
  // implies the async BNPL eligibility call has resolved. Firing earlier would
  // report `bnpl_seen: false` for checkouts that are in fact eligible,
  // silently corrupting the experiment's denominator.
  useEffect(() => {
    if (!isSheetOpen) {
      hasTrackedLdImpressionRef.current = false;
      return;
    }

    if (!isEnrolled || hasTrackedLdImpressionRef.current) return;

    hasTrackedLdImpressionRef.current = true;

    const payload: LdPaymentMethodImpressionPayload = {
      ...ldCommonPayload,
      ...buildLdSeenFlags(shownLdMethods),
    };

    Tracker.track(LD_EVENTS.paymentMethodImpression, payload, {
      whitelist: ['launchDarkly'],
    });
  }, [isSheetOpen, isEnrolled, ldCommonPayload, shownLdMethods]);

  const handleSelectMethod = (method: PaymentMethod) => {
    const isCurrentlySelected = isPaymentMethodEqual(selectedMethod, method);

    if (!isCurrentlySelected) {
      const bnplProvider =
        effectiveBnplVariant === 'control'
          ? getBnplProvider(method)
          : undefined;

      Tracker.track('payment_method_selected', {
        method: getTrackingMethod(method),
        paymentMethodType: method.type,
        paymentMethodId: method.id,
        shopId: shop?.id ?? '',
        cartId: cart?.id ?? 'none',
        ...(bnplProvider && { bnplProvider }),
      });

      if (isEnrolled) {
        const ldPayload: LdPaymentMethodSelectedPayload = {
          ...ldCommonPayload,
          method: getTrackingMethod(method),
          bnpl_provider: bnplProvider ?? BNPL_PROVIDER_UNKNOWN,
        };

        Tracker.track(LD_EVENTS.paymentMethodSelected, ldPayload, {
          whitelist: ['launchDarkly'],
        });
      }
    }

    setValue(fieldName, isCurrentlySelected ? undefined : method, {
      shouldValidate: true,
    });
  };

  const renderPaymentMethod = (method: (typeof paymentMethods)[number]) => {
    const normalizedMethod = method as PaymentMethod | UserCards[0];
    const paymentMethod: PaymentMethod = isUserCard(normalizedMethod)
      ? convertUserCardToPaymentMethod(normalizedMethod)
      : normalizedMethod;

    const isSelected = isPaymentMethodEqual(selectedMethod, paymentMethod);

    if (
      effectiveBnplVariant !== 'control' &&
      isPaymentMethodBnpl(paymentMethod)
    ) {
      return (
        <BnplPaymentMethodRow
          key={paymentMethod.id}
          testID={paymentMethod.id}
          variant={effectiveBnplVariant}
          selected={isSelected}
          onPress={() => handleSelectMethod(paymentMethod)}
          amountCents={cart?.breakdown?.outstandingChargeAmount ?? 0}
          currency={cart?.currency ?? shop?.brand?.currency ?? 'USD'}
          country={shop?.address?.country ?? 'US'}
          eligibleMethods={eligibleMethods}
        />
      );
    }

    let label: string;
    if (isPaymentMethodCardSaved(paymentMethod)) {
      label = `•••• ${paymentMethod.card.last4}`;
    } else if (isPaymentMethodCardGeneric(paymentMethod)) {
      label = t('creditOrDebitCard');
    } else if (paymentMethod.type === PaymentMethodType.payInPerson) {
      label = t('payInPerson');
    } else if (paymentMethod.type === PaymentMethodType.applePay) {
      label = t('applePay');
    } else if (paymentMethod.type === PaymentMethodType.googlePay) {
      label = t('googlePay');
    } else if (isPaymentMethodBnpl(paymentMethod)) {
      label = t('buyNowPayLaterWith', {
        provider: t(BNPL_PROVIDER_LABEL_KEYS[paymentMethod.providers[0]]),
      });
    } else {
      label = t('cashApp');
    }

    const icon = getPaymentMethodIcon(
      isUserCard(normalizedMethod) ? normalizedMethod : paymentMethod,
    );
    const key = getPaymentMethodKey(normalizedMethod);

    return (
      <PaymentMethodOption
        key={key}
        id={key}
        icon={icon}
        label={label}
        selected={isSelected}
        onPress={() => handleSelectMethod(paymentMethod)}
      />
    );
  };

  const isPayLaterMethod = (method: (typeof paymentMethods)[number]) => {
    const normalized = method as PaymentMethod | UserCards[0];
    if (isUserCard(normalized)) return false;

    return (
      isPaymentMethodBnpl(normalized) ||
      normalized.type === PaymentMethodType.payInPerson
    );
  };

  const notices = (
    <>
      {showPrepaymentNotice && !isWaitlist && (
        <InfoNotice message={t('yourCardWillBeChargedToday')} />
      )}

      {waitlistRequiresPayment && isWaitlist && (
        <InfoNotice message={t('paymentRequiredForWaitlist')} />
      )}
    </>
  );

  if (effectiveBnplVariant === 'now-later') {
    const payNowMethods = paymentMethods.filter(
      (method) => !isPayLaterMethod(method),
    );
    const payLaterMethods = paymentMethods.filter(isPayLaterMethod);

    return (
      <VStack space={theme.spacing[5]}>
        <PaymentMethodSection
          title={t('payNow')}
          testID="payment-section-pay-now"
        >
          {payNowMethods.map((method) => (
            <Fragment
              key={getPaymentMethodKey(method as PaymentMethod | UserCards[0])}
            >
              {renderPaymentMethod(method)}
            </Fragment>
          ))}
        </PaymentMethodSection>

        <PaymentMethodSection
          title={t('payLater')}
          testID="payment-section-pay-later"
        >
          {payLaterMethods.map((method) => (
            <Fragment
              key={getPaymentMethodKey(method as PaymentMethod | UserCards[0])}
            >
              {renderPaymentMethod(method)}
            </Fragment>
          ))}
        </PaymentMethodSection>

        {notices}
      </VStack>
    );
  }

  return (
    <VStack space={theme.spacing[4]}>
      {!hideSectionTitle && (
        <Text variant="lStrong" color="textPrimary">
          {t('paymentMethod')}
        </Text>
      )}

      <VStack space={theme.spacing[3]}>
        {paymentMethods.map((method) => (
          <Fragment
            key={getPaymentMethodKey(method as PaymentMethod | UserCards[0])}
          >
            {renderPaymentMethod(method)}
          </Fragment>
        ))}

        {notices}
      </VStack>
    </VStack>
  );
};

PaymentMethodSelector.displayName = 'PaymentMethodSelector';
export { PaymentMethodSelector };
